import type React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import {
  Search,
  Play,
  Pause,
  Volume2,
  Maximize,
  SkipBack,
  SkipForward,
  Share2,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  BookOpen,
  FileText,
  ClipboardCheck,
  GraduationCap,
  MessageSquare,
  AlertCircle,
  Download,
  Eye,
  CheckCircle,
  XCircle,
  X,
  Video,
  Image as ImageIcon,
  Code,
  Circle,
  CheckCircle2,
  Clock,
  Sparkles,
  Bot,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ApiService } from "../../services/api";
import ConfirmModal from "../../components/modals/ConfirmModal";
import checkPermission from "../../utils/permission";
import LessonQAView from "./components/interactions/LessonQAView";
import LessonCommentsView from "./components/interactions/LessonCommentsView";
import LessonNotesView from "./components/interactions/LessonNotesView";
import ContentViewer from "./components/content/ContentViewer";
import LessonHomeworkView from "./components/homework/LessonHomeworkView";

export const VideoPlayerPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lesson, setLesson] = useState<any>(null);
  const [currentSection, setCurrentSection] = useState<any>(null);
  const [currentContent, setCurrentContent] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(1440);
  const [activeContentTab, setActiveContentTab] = useState("notes");
  const [showControls, setShowControls] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<number, boolean>>({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    return window.innerWidth >= 768;
  });
  const [sectionGains, setSectionGains] = useState<Record<string, any[]>>({});
  const canQaList = checkPermission("lesson_qa_list");
  const canNotes = checkPermission("lesson_note");

  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const memoizedContent = useMemo(() => {
    if (!currentContent) return null;
    return {
      ...currentContent,
      lessonId,
      sectionId: currentSection?.id,
    };
  }, [currentContent, lessonId, currentSection?.id]);

  const scrollToSection = (sectionIndex: number) => {
    const sectionElement = sectionRefs.current[sectionIndex];
    if (sectionElement) {
      sectionElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleTrackingUpdate = async (contentId: string) => {
    if (!lessonId) return;
    try {
      const res = await ApiService.getLessonPreview(lessonId);
      setLesson(res);
      
      let targetSection: any = null;
      let isLastTestInSection = false;
      
      for (const section of (res?.sections || [])) {
        const content = section?.contents?.find((c: any) => c.id === contentId);
        if (content) {
          targetSection = section;
          const tests = section?.contents?.filter((c: any) => 
            c.type?.toUpperCase() === "TEST" || c.type?.toUpperCase() === "EXAM"
          ) || [];
          if (tests.length > 0) {
            const lastTest = tests[tests.length - 1];
            isLastTestInSection = lastTest.id === contentId;
          }
          break;
        }
      }
      
      for (const section of (res?.sections || [])) {
        if (isSectionTestsCompleted(section)) {
          const response = await ApiService.getUserGains({
            lessonId: lessonId,
            isResolved: false,
          });
          const sectionGainsList = response?.data?.filter((g: any) => 
            g.topic?.id === section.id
          ) || [];
          setSectionGains(prev => ({ ...prev, [section.id]: sectionGainsList }));
          
          if (targetSection?.id === section.id && isLastTestInSection && sectionGainsList.length > 0) {
            window.dispatchEvent(new CustomEvent("toggleDeficiencyTutorPanel", {
              detail: {
                gainId: sectionGainsList[0]?.id,
                allGains: sectionGainsList,
                onGainResolved: () => loadSectionGains(section.id)
              }
            }));
          }
        }
      }
    } catch (error) {
      // Lesson verisi güncellenemedi
    }
  };

  const isSectionTestsCompleted = (section: any): boolean => {
    const tests = section?.contents?.filter((c: any) => 
      c.type?.toUpperCase() === "TEST" || c.type?.toUpperCase() === "EXAM"
    ) || [];
    if (tests.length === 0) return false;
    return tests.every((test: any) => test.tracking?.status === "COMPLETED");
  };

  const loadSectionGains = async (sectionId: string) => {
    try {
      const response = await ApiService.getUserGains({
        lessonId: lessonId,
        isResolved: false,
      });
      const sectionGainsList = response?.data?.filter((g: any) => 
        g.topic?.id === sectionId
      ) || [];
      setSectionGains(prev => ({ ...prev, [sectionId]: sectionGainsList }));
    } catch (err) {
      setSectionGains(prev => ({ ...prev, [sectionId]: [] }));
    }
  };

  const checkAndLoadSectionGains = (section: any) => {
    if (isSectionTestsCompleted(section) && !sectionGains[section.id]) {
      loadSectionGains(section.id);
    }
  };

  const handleOpenTutorForSection = (sectionId: string) => {
    const gains = sectionGains[sectionId] || [];
    if (gains.length > 0) {
      window.dispatchEvent(new CustomEvent("toggleDeficiencyTutorPanel", {
        detail: {
          gainId: gains[0]?.id,
          allGains: gains,
          onGainResolved: () => loadSectionGains(sectionId)
        }
      }));
    }
  };

  const [notes, setNotes] = useState<
    Array<{
      id: string;
      content: string;
      createdAt?: string;
      sectionId?: string;
    }>
  >([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [attachToCurrentSection, setAttachToCurrentSection] = useState(true);
  const [noteDeleteOpen, setNoteDeleteOpen] = useState(false);
  const [noteDeleteId, setNoteDeleteId] = useState<string | null>(null);
  const [noteDeleteLoading, setNoteDeleteLoading] = useState(false);
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isPlaying && currentTime < duration) {
      interval = setInterval(() => {
        setCurrentTime((prevTime) => {
          if (prevTime >= duration) {
            setIsPlaying(false);
            return duration;
          }
          return prevTime + 1;
        });
      }, 1000);
    } else if (!isPlaying) {
      if (interval) {
        clearInterval(interval);
      }
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying, currentTime, duration]);
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;
    if (isPlaying && showControls) {
      timeout = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isPlaying, showControls]);

  const getContentTypeBadgeClass = (type: string) => {
    switch ((type || "").toUpperCase()) {
      case "VIDEO":
        return "bg-purple-100 text-purple-700";
      case "AUDIO":
        return "bg-blue-100 text-blue-700";
      case "IMAGE":
        return "bg-green-100 text-green-700";
      case "TEXT":
        return "bg-gray-100 text-gray-700";
      case "FILE":
      case "PDF":
        return "bg-red-100 text-red-700";
      case "CODE":
        return "bg-yellow-100 text-yellow-700";
      case "TEST":
      case "EXAM":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "VIDEO":
        return <Video className="w-4 h-4" />;
      case "AUDIO":
        return <Volume2 className="w-4 h-4" />;
      case "IMAGE":
        return <ImageIcon className="w-4 h-4" />;
      case "TEXT":
        return <FileText className="w-4 h-4" />;
      case "FILE":
      case "PDF":
        return <FileText className="w-4 h-4" />;
      case "CODE":
        return <Code className="w-4 h-4" />;
      case "TEST":
      case "EXAM":
        return <ClipboardCheck className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getProgressIcon = (content: any) => {
    if (content.tracking == null) {
      return <Circle className="w-4 h-4 text-gray-400" />;
    }
    if (content.tracking.status === "COMPLETED") {
      return <CheckCircle2 className="w-4 h-4 text-green-600" />;
    }
    if (content.tracking.status === "VIEWED") {
      return <Clock className="w-4 h-4 text-yellow-600" />;
    }
    return <Circle className="w-4 h-4 text-gray-400" />;
  };

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const isCurrentlyExpanded = prev[index as keyof typeof prev];
      const newState = {
        ...prev,
        [index]: !isCurrentlyExpanded,
      };

      if (!isCurrentlyExpanded) {
        setTimeout(() => scrollToSection(index), 100);
      }

      return newState;
    });
  };

  useEffect(() => {
    const fetchLesson = async () => {
      if (!lessonId) return;
      const res = await ApiService.getLessonPreview(lessonId);
      setLesson(res);
      // İlk yüklemede bölümlerin kazanımlarını kontrol et
      res?.sections?.forEach((section: any) => {
        if (isSectionTestsCompleted(section)) {
          loadSectionGains(section.id);
        }
      });
    };
    fetchLesson();
  }, [lessonId]);

  useEffect(() => {
    if (!lessonId) return;
    if (activeContentTab !== "notes" || !canNotes) return;
    const load = async () => {
      setNotesLoading(true);
      try {
        const res = await ApiService.getLessonNotes(lessonId as string, {
          limit: 200,
          sortBy: "createdAt",
          sortOrder: "DESC",
        });
        const data = Array.isArray(res) ? res : res?.data || [];
        setNotes(data);
      } catch {
        setNotes([]);
      } finally {
        setNotesLoading(false);
      }
    };
    load();
  }, [lessonId, activeContentTab]);

  useEffect(() => {
    if (!lessonId || !currentSection?.id) return;
    if (activeContentTab !== "deficiencies") return;
    loadSectionGains(currentSection.id);
  }, [lessonId, currentSection?.id, activeContentTab]);

  const closeDeleteNote = () => {
    setNoteDeleteOpen(false);
    setNoteDeleteId(null);
    setNoteDeleteLoading(false);
  };

  const confirmDeleteNote = async () => {
    if (!lessonId || !noteDeleteId) return;
    setNoteDeleteLoading(true);
    try {
      await ApiService.deleteLessonNote(lessonId as string, noteDeleteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteDeleteId));
      closeDeleteNote();
    } finally {
      setNoteDeleteLoading(false);
    }
  };

  const selectContentById = (contentId: string) => {
    if (!lesson?.sections) return;
    for (let i = 0; i < lesson.sections.length; i++) {
      const sec = lesson.sections[i];
      const found = (sec.contents || []).find((c: any) => c.id === contentId);
      if (found) {
        setCurrentSection(sec);
        setCurrentContent(found);
        setExpandedSections({ [i]: true });
        setTimeout(() => scrollToSection(i), 100);
        return;
      }
    }
  };

  const goToContent = (sectionId: string, contentId: string) => {
    navigate(`/lessons/play/${lessonId}?contentId=${contentId}`);
    if (lesson?.sections) {
      const sectionIndex = lesson.sections.findIndex((s: any) => s.id === sectionId);
      const sec = lesson.sections[sectionIndex];
      const cnt = sec?.contents?.find((c: any) => c.id === contentId);
      if (sec) setCurrentSection(sec);
      if (cnt) {
        setCurrentContent(cnt);
      }
      if (sectionIndex !== -1) {
        setExpandedSections({ [sectionIndex]: true });
        setTimeout(() => scrollToSection(sectionIndex), 100);
      }
    }
    setActiveContentTab((prev) => prev);
  };

  useEffect(() => {
    if (!lesson) return;
    const params = new URLSearchParams(location.search);
    const cid = params.get("contentId");
    if (cid) {
      if (currentContent?.id !== cid) selectContentById(cid);
    } else if (!currentContent) {
      const firstSection = lesson.sections?.[0];
      if (firstSection) {
        setCurrentSection(firstSection);
        const firstContent = firstSection.contents?.[0];
        if (firstContent) {
          setCurrentContent(firstContent);
          setExpandedSections({ 0: true });
        }
      }
    }
  }, [lesson, location.search]);

  useEffect(() => {
    if (currentContent && currentSection && lesson) {
      localStorage.setItem(
        `lessonProgress_${lessonId}`,
        JSON.stringify({
          sectionId: currentSection.id,
          sectionTitle: currentSection.title,
          contentId: currentContent.id,
          contentTitle: currentContent.title,
          contentType: currentContent.type,
          timestamp: new Date().toISOString(),
        })
      );
    }
  }, [currentContent, currentSection, lessonId, lesson]);

  return (
    <div className="h-[88vh] bg-gray-900 flex flex-col overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate(`/lessons/detail/${lessonId}`)}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
            <span className="text-gray-400">|</span>
            <button
              onClick={() => navigate(`/lessons/detail/${lessonId}`)}
              className="font-medium text-gray-900 hover:text-purple-600 transition-colors cursor-pointer">
              {lesson?.title}
            </button>
            {currentSection && (
              <>
                <span className="text-gray-400">›</span>
                <span className="text-gray-700">{currentSection.title}</span>
              </>
            )}
            {currentContent && (
              <>
                <span className="text-gray-400">›</span>
                <span className="text-purple-600 font-medium">{currentContent.title}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden flex-col md:flex-row">
        <div className="flex-1 overflow-y-auto min-h-0">
          <div
            className="relative w-full"
            onClick={() => currentContent?.type === "VIDEO" && setShowControls(!showControls)}
            onMouseMove={() => currentContent?.type === "VIDEO" && setShowControls(true)}>
            {memoizedContent && (
              <ContentViewer content={memoizedContent} onTrackingUpdate={handleTrackingUpdate} isParentSidebarOpen={isSidebarOpen} />
            )}
          </div>

          <div className="bg-white border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide -mx-4 sm:mx-0 px-4 sm:px-0 whitespace-nowrap">
                {[
                  { id: "notes", label: "Notlar", icon: BookOpen },
                  { id: "assignments", label: "Ödevler", icon: GraduationCap },
                  ...(lesson?.isQa && canQaList ? [{ id: "qa", label: "Soru-Cevap", icon: MessageSquare }] : []),
                  {
                    id: "deficiencies",
                    label: "Eksikler",
                    icon: AlertCircle,
                  },
                  { id: "comments", label: "Yorumlar", icon: MessageSquare },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveContentTab(tab.id)}
                      className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                        activeContentTab === tab.id
                          ? "border-purple-500 text-purple-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-gray-50">
            {activeContentTab === "notes" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <LessonNotesView lessonId={lessonId!} sections={lesson?.sections || []} defaultSectionId={currentSection?.id} />
                </div>
              </div>
            )}

            <ConfirmModal
              isOpen={noteDeleteOpen}
              onClose={closeDeleteNote}
              onConfirm={confirmDeleteNote}
              title="Notu Sil"
              message="Bu notu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
              isLoading={noteDeleteLoading}
            />

            {activeContentTab === "qa" && lesson?.isQa && canQaList && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <LessonQAView lessonId={lessonId!} sections={lesson?.sections || []} defaultSectionId={currentSection?.id} />
                </div>
              </div>
            )}

            {activeContentTab === "deficiencies" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Eksikler</h2>
                      {currentSection && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100 hidden sm:inline-block">
                          {currentSection.title}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate("/lessons?tab=deficiencies")}
                      className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline flex items-center gap-1 flex-shrink-0">
                      <span className="hidden sm:inline">Tüm Eksikleri Görüntüle</span>
                      <span className="sm:hidden">Tümünü Gör</span>
                      <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" />
                    </button>
                  </div>

                  {(sectionGains[currentSection?.id || ""] || []).length > 0 ? (
                    <div className="space-y-6">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                        <div className="flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                           <div className="w-16 h-16 bg-white rounded-full shadow-sm flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-8 h-8 text-purple-600" />
                           </div>
                           <div className="flex-1">
                              <h3 className="text-lg font-bold text-gray-900 mb-1">AI ile Eksiklerini Kapat</h3>
                              <p className="text-gray-600">
                                Bu bölümde tespit edilen <strong>{(sectionGains[currentSection?.id || ""] || []).length}</strong> adet eksiğin var.
                                <span className="block mt-1">Yapay zeka destekli özel ders ile bunları hızlıca giderebilirsin.</span>
                              </p>
                           </div>
                           <button
                             onClick={() => currentSection?.id && handleOpenTutorForSection(currentSection.id)}
                             className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold shadow-lg hover:bg-purple-700 transition-all transform hover:scale-105 flex items-center gap-2 flex-shrink-0"
                           >
                             <Sparkles className="w-5 h-5" />
                             Hemen Başla
                           </button>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <h3 className="font-semibold text-gray-900 ml-1">Tespit Edilen Eksikler Listesi</h3>
                        {(sectionGains[currentSection?.id || ""] || []).map((gain: any, index: number) => (
                           <div key={gain.id} className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-purple-200 transition-colors">
                              <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center font-bold text-purple-600 flex-shrink-0">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                 <p className="font-medium text-gray-900 mb-1 truncate">{gain.gainText || "İsimsiz Kazanım"}</p>
                                 <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">

                                    {gain.createdAt && (
                                      <span className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                                         <Clock className="w-3 h-3" />
                                         {new Date(gain.createdAt).toLocaleDateString('tr-TR')}
                                      </span>
                                    )}
                                 </div>
                              </div>
                              <div className="flex flex-col gap-2 sm:self-center self-start">
                                <button
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent("toggleDeficiencyTutorPanel", {
                                      detail: {
                                        gainId: gain.id,
                                        allGains: [gain], // Sadece bu gain için aç
                                        onGainResolved: () => currentSection?.id && loadSectionGains(currentSection.id)
                                      }
                                    }));
                                  }}
                                  className="text-sm font-medium text-purple-600 hover:text-purple-700 hover:underline px-4 py-2"
                                >
                                  İncele & Çöz
                                </button>
                                <button
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent("toggleAITutorPanel", {
                                      detail: {
                                        context: {
                                          gainId: gain.id,
                                          gainText: gain.gainText || "",
                                          contentId: currentContent?.id || "",
                                          contentTitle: currentContent?.title || "",
                                          topicId: currentSection?.id || "",
                                          topicTitle: currentSection?.title || "",
                                          lessonId: lessonId || "",
                                          lessonTitle: lesson?.title || "",
                                          startTime: gain.startTime || "",
                                          endTime: gain.endTime || "",
                                          pdfUrl: ["FILE", "PDF"].includes(String(currentContent?.type || "").toUpperCase())
                                            ? currentContent?.media?.url || ""
                                            : "",
                                        }
                                      }
                                    }));
                                  }}
                                  className="inline-flex items-center justify-center gap-2 text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg px-4 py-2"
                                >
                                  <Bot className="w-4 h-4" />
                                  AI Öğretmen Destek
                                </button>
                              </div>
                           </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-6 sm:p-8 text-center border-2 border-dashed border-gray-200">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                         <CheckCircle2 className="w-8 h-8 text-green-500" />
                      </div>
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Harika Gidiyorsun!</h3>
                      <p className="text-sm sm:text-base text-gray-500 mb-4 sm:mb-6">
                        Bu bölümde tespit edilen herhangi bir eksiğin bulunmuyor.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeContentTab === "comments" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <LessonCommentsView
                    lessonId={lessonId!}
                    sections={lesson?.sections || []}
                    fixedScope={currentSection?.id ? { type: "section", sectionId: currentSection.id } : { type: "lesson" }}
                  />
                </div>
              </div>
            )}

            {activeContentTab === "assignments" && (
              <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex-shrink-0">Ödevler</h2>
                      {currentSection && (
                        <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                          {currentSection.title}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate("/lessons?tab=homeworks")}
                      className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium hover:underline flex items-center gap-1 flex-shrink-0">
                      <span className="hidden sm:inline">Tüm Ödevleri Görüntüle</span>
                      <span className="sm:hidden">Tümünü Gör</span>
                      <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 rotate-180" />
                    </button>
                  </div>
                  <LessonHomeworkView lessonId={lessonId!} sectionId={currentSection?.id} />
                </div>
              </div>
            )}
          </div>
        </div>
        {isSidebarOpen && (
          <div className="w-full md:w-80 bg-white border-l md:border-l border-t md:border-t-0 border-gray-200 flex flex-col overflow-hidden max-h-[50vh] md:max-h-none">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Ders İçeriği</h2>
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                title="Sidebar'ı Kapat">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-1">
                {lesson?.sections?.map((section: any, index: number) => (
                  <div
                    key={index}
                    ref={(el) => {
                      sectionRefs.current[index] = el;
                    }}>
                    <button
                      onClick={() => toggleSection(index)}
                      className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors">
                      <div className="flex flex-col items-start w-full">
                        <div className="flex items-center w-full">
                          {expandedSections[index as keyof typeof expandedSections] ? (
                            <ChevronUp className="w-4 h-4 mr-2" />
                          ) : (
                            <ChevronDown className="w-4 h-4 mr-2" />
                          )}
                          <span className="font-medium text-sm">
                            Bölüm {index + 1}: {section.title}
                          </span>
                        </div>
                        <div className="ml-6 mt-2">
                          {(() => {
                            const contentTypes: Record<
                              string,
                              {
                                total: number;
                                completed: number;
                                icon: string;
                                color: string;
                              }
                            > = {};

                            section?.contents?.forEach((content: any) => {
                              const type = content.type?.toUpperCase() || "TEXT";
                              const typeName =
                                type === "VIDEO"
                                  ? "video"
                                  : type === "AUDIO"
                                  ? "ses"
                                  : type === "IMAGE"
                                  ? "resim"
                                  : type === "TEXT"
                                  ? "metin"
                                  : type === "FILE" || type === "PDF"
                                  ? "dosya"
                                  : type === "CODE"
                                  ? "kod"
                                  : type === "TEST" || type === "EXAM"
                                  ? "test"
                                  : "içerik";

                              const icon =
                                type === "VIDEO"
                                  ? "🎥"
                                  : type === "AUDIO"
                                  ? "🎵"
                                  : type === "IMAGE"
                                  ? "🖼️"
                                  : type === "TEXT"
                                  ? "📝"
                                  : type === "FILE" || type === "PDF"
                                  ? "📁"
                                  : type === "CODE"
                                  ? "💻"
                                  : type === "TEST" || type === "EXAM"
                                  ? "📋"
                                  : "📄";

                              const color =
                                type === "VIDEO"
                                  ? "bg-purple-100 text-purple-700"
                                  : type === "AUDIO"
                                  ? "bg-blue-100 text-blue-700"
                                  : type === "IMAGE"
                                  ? "bg-green-100 text-green-700"
                                  : type === "TEXT"
                                  ? "bg-gray-100 text-gray-700"
                                  : type === "FILE" || type === "PDF"
                                  ? "bg-red-100 text-red-700"
                                  : type === "CODE"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : type === "TEST" || type === "EXAM"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-700";

                              if (!contentTypes[typeName]) {
                                contentTypes[typeName] = {
                                  total: 0,
                                  completed: 0,
                                  icon,
                                  color,
                                };
                              }

                              contentTypes[typeName].total++;

                              if (content.tracking?.status === "COMPLETED") {
                                contentTypes[typeName].completed++;
                              }
                            });

                            const totalCount = section?.contents?.length || 0;
                            const totalCompleted =
                              section?.contents?.filter((content: any) => content.tracking?.status === "COMPLETED").length || 0;

                            if (totalCount === 0) return <div className="text-xs text-gray-400 italic">İçerik yok</div>;

                            const progressPercentage = totalCount > 0 ? Math.round((totalCompleted / totalCount) * 100) : 0;

                            return (
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-48 bg-gray-200 rounded-full h-1.5">
                                    <div
                                      className="bg-green-500 h-1.5 rounded-full transition-all duration-300"
                                      style={{
                                        width: `${progressPercentage}%`,
                                      }}
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-gray-600">%{progressPercentage}</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                  {Object.entries(contentTypes).map(([type, data], index) => (
                                    <span key={type}>
                                      <span className="font-medium">
                                        {data.completed}/{data.total}
                                      </span>{" "}
                                      {type}
                                      {index < Object.entries(contentTypes).length - 1 && ", "}
                                    </span>
                                  ))}
                                  {" (toplam "}
                                  <span className="font-medium">
                                    {totalCompleted}/{totalCount}
                                  </span>
                                  {" içerik)"}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </button>
                    {expandedSections[index as keyof typeof expandedSections] && section?.contents?.length > 0 && (
                      <div className="ml-6 space-y-1">
                        {section?.contents?.map((contentItem: any, lessonIndex: number) => (
                          <div
                            key={lessonIndex}
                            className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                              currentContent?.id === contentItem.id ? "bg-purple-50 text-purple-600" : "hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              setIsPlaying(false);
                              setCurrentTime(0);
                              goToContent(section.id, contentItem.id);
                            }}>
                            <div className="flex items-center">
                              <div className="mr-2 flex items-center justify-center flex-shrink-0">{getProgressIcon(contentItem)}</div>
                              <div
                                className={`w-7 h-7 rounded-md mr-2 flex items-center justify-center flex-shrink-0 ${getContentTypeBadgeClass(
                                  contentItem.type || "TEXT"
                                )}`}>
                                {getContentTypeIcon(contentItem.type || "TEXT")}
                              </div>
                              <span className="text-sm line-clamp-1 sm:line-clamp-none min-w-0 flex-1">{contentItem.title}</span>
                            </div>
                            <span className="text-xs text-gray-500">{contentItem.duration}</span>
                          </div>
                        ))}
                        
                        {/* Eksiğini Gider Butonu - Bölümdeki tüm testler tamamlandıysa ve eksik kazanım varsa */}
                        {isSectionTestsCompleted(section) && (sectionGains[section.id]?.length || 0) > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenTutorForSection(section.id);
                            }}
                            className="w-full mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 border border-purple-600 transition-colors"
                          >
                            <Sparkles className="w-4 h-4" />
                            <span>AI ile Eksiklerini Gider ({sectionGains[section.id]?.length})</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-all duration-200 z-10 flex items-center gap-2"
            title="Ders İçeriğini Göster">
            <ChevronUp className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">İçerik</span>
          </button>
        )}
      </div>

      {/* DeficiencyTutorPanel artık global olarak Layouts.jsx'te yönetiliyor */}
    </div>
  );
};
